/**
 * Phase 4 — Email-to-case (G.15). Client for a case's ingested email history + manual ingest trigger.
 */
export interface CaseEmailDto {
  CaseEmail_guid: string;
  CaseEmail_Subject: string | null;
  CaseEmail_From: string | null;
  CaseEmail_To: string | null;
  CaseEmail_Preview: string | null;
  CaseEmail_Direction: string;
  CaseEmail_Mailbox: string;
  CaseEmail_MatchedBy: string | null;
  CaseEmail_ReceivedAt: string | null;
  /**
   * Why no automatic CDL answer was written. Both absent from an older API, so both are optional
   * and the panel simply shows nothing when they do not arrive.
   */
  CaseEmail_CdlRefusal?: string | null;
  CaseEmail_CdlRefusalExplanation?: string | null;
}

/** Must match MailboxKey in backend/src/utils/emailToCase/caseEmailIngestion.ts. */
export type MailboxKey = 'court' | 'client' | 'cdl';

export interface IngestSummary {
  mailbox: string;
  scanned: number;
  matched: number;
  stored: number;
  skippedExisting: number;
  unmatched: number;
  /** CDL only, and absent from an older API. Guard before reading. */
  queued?: number;
  drafted?: number;
  sent?: number;
  unanswered?: string[];
}

function host(): string {
  return process.env.API_HOST?.trim() || window.location.origin;
}

class CaseEmailApi {
  static async listForCase(ticketGuid: string): Promise<CaseEmailDto[]> {
    const response = await fetch(`${host()}/api/v1/manager/ticket/${ticketGuid}/emails`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to load case emails');
    return response.json();
  }

  /** Manual ingest trigger. Returns 409 (thrown) when that mailbox's switch is off. */
  static async ingest(mailbox: MailboxKey, limit = 50): Promise<IngestSummary> {
    const response = await fetch(`${host()}/api/v1/manager/ingest-emails`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ mailbox, limit }),
    });
    if (response.status === 409) {
      // The server says WHICH switch is off, and each mailbox has its own. A hardcoded message here
      // used to blame ENABLE_EMAIL_TO_CASE for a CDL refusal, sending staff to the wrong setting.
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'That mailbox sweep is switched off.');
    }
    if (!response.ok) throw new Error('Failed to ingest emails');
    return response.json();
  }
}

export default CaseEmailApi;
